import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import bem from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { TableCellModifier } from './TableCell';

export const TableModifier = {
  XXSWIDTH: TableCellModifier.XXS_WIDTH,
  SWIDTH: TableCellModifier.S_WIDTH,
  MWIDTH: TableCellModifier.M_WIDTH,
  LWIDTH: TableCellModifier.L_WIDTH,
  XLWIDTH: TableCellModifier.XL_WIDTH,
  WIDTH_140: TableCellModifier.WIDTH_140,
  LOWPADDING: TableCellModifier.LOWPADDING,
  DARKBORDER: TableCellModifier.DARK_BORDER,
} as const;

export enum TablePageModifier {
  SHARED_FILES = 'shared-files',
  CALENDAR = 'calendar',
}

type Table = {
  caption: string | React.ReactNode;
  removeOnPaste?: boolean;
  captionHidden?: boolean;
  id?: string;
  modifier?: Modifier<TablePageModifier>;
  cssClass?: string;
  customTableClass?: string;
};
export function Table({
  children,
  caption,
  id,
  removeOnPaste,
  modifier,
  cssClass,
  captionHidden = true,
  customTableClass,
}: PropsWithChildren<Table>) {
  // tabindex="0" required -> overflowing content can not be accessed via keyboard, if there is no tabindex.
  const [tabIndex, setTabindex] = useState<number>();
  const responsiveRef = useRef<HTMLDivElement>(null);
  const uniqueId = useHtmlValidId();
  const labeledById = `table-caption-${uniqueId}`;

  useEffect(() => {
    const responsiveContainer = responsiveRef;
    // scrollWidth > offsetWidth is an indicator, that the div is scrollable horizontally
    // which happens when the content is overflowing the container

    if (
      responsiveContainer.current &&
      responsiveContainer.current.scrollWidth > responsiveContainer.current.offsetWidth
    ) {
      setTabindex(0);
    }
  }, []);

  const cssClasses = ['responsive-tables', cssClass].join(' ').trim();

  return (
    <section className={cssClasses} tabIndex={tabIndex} ref={responsiveRef} aria-labelledby={labeledById}>
      <table className={customTableClass || bem('tables', modifier)} data-remove-on-paste={removeOnPaste} id={id}>
        {caption && (
          <caption className={captionHidden === true ? 'invisible' : 'tables__caption'} id={labeledById}>
            {caption}
          </caption>
        )}
        {children}
      </table>
    </section>
  );
}
